import { useState, useEffect, useCallback } from 'react';

export interface Message {
  type: 'ai' | 'user' | 'system';
  text: string;
  time?: string;
  id?: number;
}

const DB_NAME = 'ai_inochan_db';
const DB_VERSION = 1;
const STORE_NAME = 'chat_history';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('type', 'type', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function loadMessages(): Promise<Message[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as Message[]);
    req.onerror = () => reject(req.error);
  });
}

async function saveMessage(msg: Omit<Message, 'id'>): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.add(msg);
    req.onsuccess = () => resolve(req.result as number);
    req.onerror = () => reject(req.error);
  });
}

async function clearAllMessages(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function useChatHistory(initialMessages: Message[] = []) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadMessages().then((saved) => {
      if (saved.length > 0) {
        setMessages(saved);
      } else {
        setMessages(initialMessages);
        // 初期メッセージを保存
        Promise.all(initialMessages.map((m) => saveMessage(m))).catch(() => {});
      }
      setLoaded(true);
    }).catch(() => {
      setMessages(initialMessages);
      setLoaded(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMessage = useCallback(async (msg: Omit<Message, 'id'>) => {
    const id = await saveMessage(msg).catch(() => Date.now());
    setMessages((prev) => [...prev, { ...msg, id }]);
  }, []);

  const resetHistory = useCallback(async (newMessages: Message[] = []) => {
    await clearAllMessages().catch(() => {});
    const saved: Message[] = [];
    for (const m of newMessages) {
      const id = await saveMessage(m).catch(() => Date.now());
      saved.push({ ...m, id });
    }
    setMessages(saved);
  }, []);

  return { messages, setMessages, addMessage, resetHistory, loaded };
}
